import { CSSProperties, useCallback, useEffect, useRef, useState } from "react"
import { Box, Stack, Typography } from "@mui/material"
import { Elements, PaymentRequestButtonElement, useStripe } from "@stripe/react-stripe-js"
import { PaymentRequest, PaymentRequestPaymentMethodEvent, PaymentRequestWallet } from "@stripe/stripe-js"
import { TProductTypePayment, TWalletProviderEnum } from "../../core/types/Product"
import { useCheckoutContext } from "../../core/hooks/useCheckoutContext"
import { useGatewayContext } from "../../core/contexts/GatewayContext"
import {
  buildPagarMePaymentDataRequest,
  createGooglePaymentsClient,
  GOOGLE_PAY_READY_TO_PAY_REQUEST,
  loadGooglePaySDK,
} from "../../core/functions/GooglePayFunctions"
import {
  buildApplePayPaymentRequest,
  createApplePaySession,
  isApplePaySupported,
} from "../../core/functions/ApplePayFunctions"
import { ApplePayController } from "../../core/controllers/ApplePayController"
import { getResponseError } from "../../services/api"
import { useTranslation } from "react-i18next"

export interface IWalletPaymentButtonsProps {
  amount: number
}

const WalletDivider = () => {
  const { t } = useTranslation()

  return (
    <Stack direction="row" alignItems="center" spacing={1.5}>
      <Box sx={{ flex: 1, height: "1px", bgcolor: "divider" }} />
      <Typography variant="body2" sx={{ color: "text.secondary", whiteSpace: "nowrap" }}>
        {t("wallet.orPayWithCard")}
      </Typography>
      <Box sx={{ flex: 1, height: "1px", bgcolor: "divider" }} />
    </Stack>
  )
}

// Link e cartão salvo no navegador não são wallets que o backend aceita (ele só
// conhece Apple Pay e Google Pay) e a wallet desligada na oferta seria recusada
// com CHECKOUT_WALLET_DISABLED — então o sheet só oferece o que está habilitado.
export const getStripeDisabledWallets = (
  enableApplePay: boolean,
  enableGooglePay: boolean
): PaymentRequestWallet[] => [
  "link",
  "browserCard",
  ...(enableApplePay ? [] : ["applePay" as const]),
  ...(enableGooglePay ? [] : ["googlePay" as const]),
]

const StripeWalletButton = ({
  amount,
  currency,
  enableApplePay,
  enableGooglePay,
  onReady,
}: {
  amount: number
  currency: string
  enableApplePay: boolean
  enableGooglePay: boolean
  onReady: (ready: boolean) => void
}) => {
  const stripe = useStripe()
  const { submitWalletPayment } = useGatewayContext()
  const [paymentRequest, setPaymentRequest] = useState<PaymentRequest | null>(null)

  useEffect(() => {
    onReady(!!paymentRequest)
  }, [paymentRequest, onReady])

  useEffect(() => {
    // Preço ainda não carregou (amount chega 0 antes do productPrice resolver) —
    // um PaymentRequest com total zerado é rejeitado/incoerente. Ver minor finding.
    if (!stripe || amount <= 0) return
    let mounted = true

    const pr = stripe.paymentRequest({
      country: process.env.REACT_APP_STRIPE_ACCOUNT_COUNTRY || "US",
      currency: currency.toLowerCase(),
      total: { label: "Total", amount: Math.round(amount * 100) },
      requestPayerName: true,
      requestPayerEmail: true,
      disableWallets: getStripeDisabledWallets(enableApplePay, enableGooglePay),
    })

    pr.canMakePayment().then((result) => {
      if (mounted && result) setPaymentRequest(pr)
    })

    const handlePaymentMethod = async (ev: PaymentRequestPaymentMethodEvent) => {
      if (ev.walletName !== "applePay" && ev.walletName !== "googlePay") {
        ev.complete("fail")
        return
      }
      const success = await submitWalletPayment({
        walletProvider:
          ev.walletName === "applePay" ? TWalletProviderEnum.ApplePay : TWalletProviderEnum.GooglePay,
        walletToken: ev.paymentMethod.id,
        walletCardMetadata: {
          brand: ev.paymentMethod.card?.brand,
          last4: ev.paymentMethod.card?.last4,
        },
      })
      ev.complete(success ? "success" : "fail")
    }

    pr.on("paymentmethod", handlePaymentMethod)

    return () => {
      mounted = false
      pr.off("paymentmethod", handlePaymentMethod)
    }
  }, [stripe, amount, currency, enableApplePay, enableGooglePay, submitWalletPayment])

  if (!paymentRequest) return null

  return <PaymentRequestButtonElement options={{ paymentRequest }} />
}

const GooglePayButton = ({
  amount,
  currency,
  onReady,
}: {
  amount: number
  currency: string
  onReady: (ready: boolean) => void
}) => {
  const { submitWalletPayment } = useGatewayContext()
  const { t, i18n } = useTranslation()
  const [ready, setReady] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  // pt-BR segue como sempre foi; nos outros idiomas o botão usa o código de 2 letras.
  const buttonLocale = i18n.resolvedLanguage === "pt-BR" ? "pt-BR" : (i18n.resolvedLanguage ?? "pt-BR")

  const environment = (process.env.REACT_APP_GOOGLE_PAY_ENVIRONMENT as "TEST" | "PRODUCTION") || ""
  const gatewayMerchantId = process.env.REACT_APP_PAGARME_GATEWAY_MERCHANT_ID || ""
  // Sem essas duas envs (Merchant ID aprovado pela Pagar.me/Google) não há como
  // montar um request válido — nem chamar isReadyToPay. Ver finding I4.
  const configured = !!environment && !!gatewayMerchantId

  // Debug interno: ?gpayExample=1 troca pro par de teste público do Google
  // (gateway "example") em vez da Pagar.me, pra tirar prints do sheet sem
  // depender da habilitação comercial da conta. Nunca documentar externamente.
  const useExampleGateway = new URLSearchParams(window.location.search).get("gpayExample") === "1"

  useEffect(() => {
    if (!configured) return
    let mounted = true
    loadGooglePaySDK()
      .then(() => createGooglePaymentsClient(environment).isReadyToPay(GOOGLE_PAY_READY_TO_PAY_REQUEST))
      .then((result) => {
        if (mounted) setReady(result.result)
      })
      .catch(() => {
        if (mounted) setReady(false)
      })
    return () => {
      mounted = false
    }
  }, [configured, environment])

  useEffect(() => {
    onReady(configured && ready)
  }, [configured, ready, onReady])

  const handleClick = useCallback(async () => {
    try {
      const client = createGooglePaymentsClient(environment)
      const request = buildPagarMePaymentDataRequest({
        gateway: useExampleGateway ? "example" : undefined,
        gatewayMerchantId: useExampleGateway ? "exampleGatewayMerchantId" : gatewayMerchantId,
        googleMerchantId: useExampleGateway
          ? undefined
          : process.env.REACT_APP_GOOGLE_MERCHANT_ID || undefined,
        merchantName: "Celetus",
        totalPrice: amount.toFixed(2),
        currencyCode: currency,
        countryCode: "BR",
      })
      const paymentData = await client.loadPaymentData(request)
      // Token do gateway "example" não é processável pelo backend — só serve
      // pra tirar o print do sheet. Não tenta submeter o pedido nesse modo.
      if (useExampleGateway) return
      await submitWalletPayment({
        walletProvider: TWalletProviderEnum.GooglePay,
        walletToken: paymentData.paymentMethodData.tokenizationData.token,
        walletCardMetadata: {
          brand: paymentData.paymentMethodData.info?.cardNetwork,
          last4: paymentData.paymentMethodData.info?.cardDetails,
        },
      })
    } catch (error: any) {
      if (error?.statusCode === "CANCELED") return
      // eslint-disable-next-line no-console
      console.error(error)
      await submitWalletPayment({ error: t("wallet.googlePayError") })
    }
  }, [amount, currency, environment, gatewayMerchantId, useExampleGateway, submitWalletPayment, t])

  // Botão oficial do Google (client.createButton) — exigido pelas diretrizes de
  // marca do Google Pay, não pode ser um botão custom. Ver finding I8.
  // buttonRadius/buttonSizeMode/buttonColor/buttonType são opções nativas da API
  // pra alinhar com o resto do checkout, sem precisar de CSS custom.
  useEffect(() => {
    if (!ready || !containerRef.current) return
    const client = createGooglePaymentsClient(environment)
    const button = client.createButton({
      onClick: handleClick,
      buttonSizeMode: "fill",
      buttonRadius: 8,
      buttonColor: "black",
      buttonType: "pay",
      buttonLocale,
    })
    containerRef.current.appendChild(button)
    return () => {
      containerRef.current?.removeChild(button)
    }
  }, [ready, environment, handleClick, buttonLocale])

  if (!configured || !ready) return null

  return <div ref={containerRef} data-testid="googlePayButton" style={{ height: 44 }} />
}

const ApplePayButton = ({
  amount,
  currency,
  onReady,
}: {
  amount: number
  currency: string
  onReady: (ready: boolean) => void
}) => {
  const { submitWalletPayment } = useGatewayContext()
  const { t } = useTranslation()

  // Backend ainda não tem o certificado da Apple configurado (habilitação
  // comercial em andamento) — manter atrás desta flag até o time de backend
  // confirmar. Ver docs/superpowers/specs/2026-09-06-google-apple-pay-checkout-design.md, seção 3.
  const pagarmeEnabled = process.env.REACT_APP_APPLE_PAY_PAGARME_ENABLED === "true"
  const supported = pagarmeEnabled && isApplePaySupported()

  useEffect(() => {
    onReady(supported)
  }, [supported, onReady])

  const handleClick = useCallback(() => {
    const session = createApplePaySession(
      buildApplePayPaymentRequest({
        totalLabel: "Celetus",
        totalAmount: amount.toFixed(2),
        currencyCode: currency,
        countryCode: "BR",
      })
    )

    session.onvalidatemerchant = async (event) => {
      try {
        const merchantSession = await ApplePayController.validateMerchant(event.validationURL)
        session.completeMerchantValidation(merchantSession)
      } catch (error) {
        // O detail do backend (config/integração) é só para log — nunca mostrar pro comprador.
        // eslint-disable-next-line no-console
        console.error("Apple Pay merchant validation falhou:", getResponseError(error))
        session.abort()
        await submitWalletPayment({
          error: t("wallet.applePayError"),
        })
      }
    }

    session.onpaymentauthorized = async (event) => {
      const success = await submitWalletPayment({
        walletProvider: TWalletProviderEnum.ApplePay,
        walletToken: JSON.stringify(event.payment.token.paymentData),
        walletCardMetadata: {
          brand: event.payment.token.paymentMethod?.network,
        },
      })
      session.completePayment(
        success ? window.ApplePaySession!.STATUS_SUCCESS : window.ApplePaySession!.STATUS_FAILURE
      )
    }

    session.oncancel = () => {}

    session.begin()
  }, [amount, currency, submitWalletPayment, t])

  if (!supported) return null

  return (
    <button
      type="button"
      onClick={handleClick}
      data-testid="applePayButton"
      style={
        {
          width: "100%",
          height: 44,
          border: 0,
          borderRadius: 8,
          cursor: "pointer",
          WebkitAppearance: "-apple-pay-button",
          "--apple-pay-button-type": "buy",
          "--apple-pay-button-style": "white-outline",
        } as CSSProperties
      }
    />
  )
}

const WalletPaymentButtons = ({ amount }: IWalletPaymentButtonsProps) => {
  const { productPrice, isPurposeCardUpdate } = useCheckoutContext()
  const { useStripe: useStripePayment, stripePromise } = useGatewayContext()
  const [stripeReady, setStripeReady] = useState(false)
  const [googleReady, setGoogleReady] = useState(false)
  const [appleReady, setAppleReady] = useState(false)

  if (!productPrice) return null
  if (isPurposeCardUpdate()) return null
  if (productPrice.typePayment === TProductTypePayment.RecurringSubscription) return null

  const showGooglePay = productPrice.enableGooglePay ?? false
  const showApplePay = productPrice.enableApplePay ?? false

  if (useStripePayment) {
    if (!stripePromise || (!showGooglePay && !showApplePay)) return null
    return (
      <>
        <Elements stripe={stripePromise}>
          <StripeWalletButton
            amount={amount}
            currency={(productPrice.currency ?? "USD").toUpperCase()}
            enableApplePay={showApplePay}
            enableGooglePay={showGooglePay}
            onReady={setStripeReady}
          />
        </Elements>
        {stripeReady && <WalletDivider />}
      </>
    )
  }

  if (!showGooglePay && !showApplePay) return null

  const currency = (productPrice.currency ?? "BRL").toUpperCase()

  // Stack só entra quando os dois botões disputam espaço — no caso de um só,
  // renderiza ele direto (sem wrapper) pra não deixar uma div vazia no DOM
  // enquanto o candidato decide não se mostrar (env não configurada, device
  // sem suporte etc). O Fragment externo não gera nó no DOM.
  return (
    <>
      {showGooglePay && showApplePay ? (
        <Stack spacing={1}>
          <GooglePayButton amount={amount} currency={currency} onReady={setGoogleReady} />
          <ApplePayButton amount={amount} currency={currency} onReady={setAppleReady} />
        </Stack>
      ) : showGooglePay ? (
        <GooglePayButton amount={amount} currency={currency} onReady={setGoogleReady} />
      ) : (
        <ApplePayButton amount={amount} currency={currency} onReady={setAppleReady} />
      )}
      {(googleReady || appleReady) && <WalletDivider />}
    </>
  )
}

export default WalletPaymentButtons
